import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { Plus, Edit, Trash2, X, Search, RotateCcw, Shield, User } from 'lucide-react';
import { dashboardService, authService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

function UsersPage() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', role: 'editor' });
  const { user: currentUser } = useAuth();

  // Filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [users, searchTerm, filterRole, filterStatus]);

  const loadUsers = async () => {
    try {
      const response = await dashboardService.getUsers();
      setUsers(response.data.users || []);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let result = [...users];

    // Recherche par nom ou email
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(user =>
        user.name?.toLowerCase().includes(term) ||
        user.email?.toLowerCase().includes(term)
      );
    }

    // Filtre par rôle
    if (filterRole !== 'all') {
      result = result.filter(user => user.role === filterRole);
    }

    // Filtre par statut
    if (filterStatus === 'active') {
      result = result.filter(user => user.is_active);
    } else if (filterStatus === 'inactive') {
      result = result.filter(user => !user.is_active);
    }

    setFilteredUsers(result);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setFilterRole('all');
    setFilterStatus('all');
  };

  const hasActiveFilters = searchTerm || filterRole !== 'all' || filterStatus !== 'all';

  const openModal = (user = null) => {
    if (user) {
      setEditing(user);
      setFormData({ name: user.name, email: user.email, password: '', role: user.role });
    } else {
      setEditing(null);
      setFormData({ name: '', email: '', password: '', role: 'editor' });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const data = { name: formData.name, email: formData.email, role: formData.role };
        if (formData.password) {
          data.password = formData.password;
        }
        await dashboardService.updateUser(editing.id, data);
        toast.success('Utilisateur mis à jour');
      } else {
        await authService.register(formData);
        toast.success('Utilisateur créé');
      }
      setShowModal(false);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const toggleActive = async (user) => {
    if (user.id === currentUser?.id) {
      return toast.error('Impossible de vous désactiver');
    }
    try {
      await dashboardService.updateUser(user.id, { is_active: !user.is_active });
      toast.success('Statut mis à jour');
      loadUsers();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (id === currentUser?.id) {
      return toast.error('Impossible de supprimer votre compte');
    }
    if (!confirm('Supprimer cet utilisateur ?')) return;
    try {
      await dashboardService.deleteUser(id);
      toast.success('Utilisateur supprimé');
      loadUsers();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-dark-900">Utilisateurs</h1>
          <p className="text-dark-500">
            {filteredUsers.length} utilisateur{filteredUsers.length > 1 ? 's' : ''}
            {filteredUsers.length !== users.length && ` sur ${users.length}`}
          </p>
        </div>
        <button onClick={() => openModal()} className="btn-primary">
          <Plus className="w-5 h-5 mr-2" />
          Ajouter
        </button>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl shadow-sm border border-dark-100 p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          {/* Recherche */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-400" />
            <input
              type="text"
              placeholder="Rechercher par nom ou email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-dark-50 border-0 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Filtre par rôle */}
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="px-4 py-2 bg-dark-50 border-0 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">Tous les rôles</option>
            <option value="admin">Administrateurs</option>
            <option value="editor">Éditeurs</option>
          </select>

          {/* Filtre par statut */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 bg-dark-50 border-0 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">Tous les statuts</option>
            <option value="active">Actifs</option>
            <option value="inactive">Inactifs</option>
          </select>

          {/* Reset */}
          {hasActiveFilters && (
            <button onClick={resetFilters} className="px-4 py-2 text-dark-600 hover:bg-dark-50 rounded-lg" title="Réinitialiser">
              <RotateCcw className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-dark-100 overflow-hidden">
        <table className="w-full">
          <thead className="bg-dark-50">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-medium text-dark-600">Utilisateur</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-dark-600">Rôle</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-dark-600">Dernière connexion</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-dark-600">Statut</th>
              <th className="px-6 py-4 text-right text-sm font-medium text-dark-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-dark-100">
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-12 text-center text-dark-500">
                  {users.length === 0 ? 'Aucun utilisateur' : 'Aucun utilisateur ne correspond aux critères'}
                </td>
              </tr>
            ) : (
              filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-dark-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${user.role === 'admin' ? 'bg-primary-100 text-primary-600' : 'bg-dark-100 text-dark-600'}`}>
                        {user.name?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-dark-900 flex items-center gap-2">
                          {user.name}
                          {user.id === currentUser?.id && (
                            <span className="text-xs bg-primary-100 text-primary-600 px-2 py-0.5 rounded">Vous</span>
                          )}
                        </p>
                        <p className="text-sm text-dark-500">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium ${user.role === 'admin' ? 'bg-primary-100 text-primary-700' : 'bg-dark-100 text-dark-600'}`}>
                      {user.role === 'admin' ? <Shield className="w-3 h-3" /> : <User className="w-3 h-3" />}
                      {user.role === 'admin' ? 'Admin' : 'Éditeur'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-dark-500">
                    {user.last_login ? format(new Date(user.last_login), 'd MMM yyyy à HH:mm', { locale: fr }) : 'Jamais'}
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => toggleActive(user)}
                      disabled={user.id === currentUser?.id}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                        user.is_active
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-red-100 text-red-600 hover:bg-red-200'
                      } ${user.id === currentUser?.id ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      {user.is_active ? 'Actif' : 'Inactif'}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openModal(user)} className="p-2 text-dark-400 hover:bg-dark-50 hover:text-primary-600 rounded-lg" title="Modifier">
                        <Edit className="w-4 h-4" />
                      </button>
                      {user.id !== currentUser?.id && (
                        <button onClick={() => handleDelete(user.id)} className="p-2 text-dark-400 hover:bg-red-50 hover:text-red-600 rounded-lg" title="Supprimer">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Stats rapides */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <div className="bg-white rounded-xl p-4 border border-dark-100">
          <p className="text-2xl font-bold text-dark-900">{users.length}</p>
          <p className="text-sm text-dark-500">Total</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-dark-100">
          <p className="text-2xl font-bold text-primary-600">{users.filter(u => u.role === 'admin').length}</p>
          <p className="text-sm text-dark-500">Admins</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-dark-100">
          <p className="text-2xl font-bold text-green-600">{users.filter(u => u.is_active).length}</p>
          <p className="text-sm text-dark-500">Actifs</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-dark-100">
          <p className="text-2xl font-bold text-red-600">{users.filter(u => !u.is_active).length}</p>
          <p className="text-sm text-dark-500">Inactifs</p>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-dark-900/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl w-full max-w-md"
          >
            <div className="p-6 border-b border-dark-100 flex items-center justify-between">
              <h2 className="text-xl font-semibold">{editing ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-dark-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-dark-700 mb-1">Nom *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-2 border border-dark-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-700 mb-1">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-2 border border-dark-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-700 mb-1">
                  Mot de passe {editing ? '(laisser vide pour ne pas changer)' : '*'}
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  className="w-full px-4 py-2 border border-dark-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required={!editing}
                  minLength={6}
                  placeholder={editing ? '••••••••' : ''}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-700 mb-1">Rôle</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                  className="w-full px-4 py-2 border border-dark-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="editor">Éditeur</option>
                  <option value="admin">Administrateur</option>
                </select>
                <p className="text-xs text-dark-400 mt-1">
                  {formData.role === 'admin' ? 'Accès complet à toutes les fonctionnalités' : 'Peut créer et modifier du contenu'}
                </p>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-dark-100">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-dark-600 hover:bg-dark-50 rounded-lg">
                  Annuler
                </button>
                <button type="submit" className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
                  {editing ? 'Enregistrer' : 'Créer'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}

export default UsersPage;